import { KeyOutlined, PlusOutlined, QrcodeOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { AccountQrModal } from '@hq/account';
import type { AdminPermRow, AdminRoleRow, AdminUserRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tree,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';
import { LEVEL_LABELS, levelLabel } from '../config';
import { useAuthStore } from '../stores/auth';

interface PermNode {
  title: string;
  key: string;
  children: PermNode[];
}

/** 业务门户只建业务账号：admin/sys 不列入选项（后端也会 403）。 */
const LEVEL_OPTIONS = Object.entries(LEVEL_LABELS)
  .filter(([code]) => code !== 'admin' && code !== 'sys')
  .map(([code, text]) => ({ value: code, label: text }));

/** 人员与角色：用户维护 + 角色权限分配（sys:user:manage / sys:role:manage）。 */
export default function BasicUser() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const isAdmin = profile?.level === 'admin';
  const canUser = isAdmin || (profile?.perms ? hasPermission(profile.perms, 'sys:user:manage') : false);
  const canRole = isAdmin || (profile?.perms ? hasPermission(profile.perms, 'sys:role:manage') : false);

  // 只有角色权限时默认落到「角色」页，否则会停在一个自己无权的 tab 上
  const [tab, setTab] = useState(canUser ? 'user' : 'role');
  const [keyword, setKeyword] = useState('');
  const [size, setSize] = useState(20);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const [userOpen, setUserOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  /** 身份码弹窗目标（App 扫码确认送达/签收）。 */
  const [qrTarget, setQrTarget] = useState<AdminUserRow | null>(null);
  const [roleTarget, setRoleTarget] = useState<AdminUserRow | null>(null);
  /** 重置密码目标账号。 */
  const [resetTarget, setResetTarget] = useState<AdminUserRow | null>(null);
  const [resetForm] = Form.useForm<{ password: string }>();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [permRole, setPermRole] = useState<AdminRoleRow | null>(null);
  const [checkedPerms, setCheckedPerms] = useState<string[]>([]);
  /** 字段级屏蔽配置：permId -> 逗号分隔字段名（`sys_role_permission.field_mask`）。 */
  const [permMasks, setPermMasks] = useState<Record<string, string>>({});
  const [userForm] = Form.useForm();
  /** 邮箱查重只在失焦时触发（避免逐字符发请求）。 */
  const [emailCheck, setEmailCheck] = useState<string | undefined>();

  const users = useQuery({
    queryKey: ['basic', 'users', keyword, size],
    placeholderData: keepPreviousData,
    queryFn: () => hq.admin.users.list({ keyword: keyword || undefined, size }),
    enabled: canUser
  });

  const roles = useQuery({
    queryKey: ['basic', 'roles'],
    queryFn: () => hq.admin.roles.list(),
    // 角色列表属于 sys:role:manage：原来用 canUser 判定，
    // 只有角色管理权限的人拉不到数据、整个「角色」页也进不去（有权限却用不了）
    enabled: canRole
  });

  const perms = useQuery({
    queryKey: ['basic', 'perms'],
    queryFn: () => hq.admin.perms.list(),
    enabled: canRole && tab === 'role'
  });

  const rolePerms = useQuery({
    queryKey: ['basic', 'role-perms', permRole?.id],
    queryFn: () => hq.admin.roles.perms(permRole?.id ?? ''),
    enabled: Boolean(permRole) && canRole
  });

  const rolePermMasks = useQuery({
    queryKey: ['basic', 'role-perm-masks', permRole?.id],
    queryFn: () => hq.admin.roles.permMasks(permRole?.id ?? ''),
    enabled: Boolean(permRole) && canRole
  });

  // 新建时不传 orgId，后端回退到当前账号机构；预览不占号
  const preflight = useQuery({
    queryKey: ['basic', 'user-preflight', emailCheck],
    queryFn: () => hq.admin.users.preflight({ email: emailCheck }),
    enabled: userOpen && !editing
  });

  const emailCount = preflight.data?.emailCount ?? 0;

  useEffect(() => {
    if (rolePerms.data) setCheckedPerms(rolePerms.data);
  }, [rolePerms.data]);

  useEffect(() => {
    if (!rolePermMasks.data) return;
    const next: Record<string, string> = {};
    rolePermMasks.data.forEach((item) => {
      if (item.fieldMask) next[item.permId] = item.fieldMask;
    });
    setPermMasks(next);
  }, [rolePermMasks.data]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['basic'] });
  };

  const saveUser = useMutation({
    mutationFn: async () => {
      const values = await userForm.validateFields();
      if (editing) {
        await hq.admin.users.update(editing.id, {
          realName: values.realName,
          mobile: values.mobile,
          email: values.email,
          levelCode: values.levelCode,
          status: values.status,
          remark: values.remark
        });
        return { userCode: editing.userCode, duplicated: false };
      }
      // 账号由服务端按「机构号 + 4 位序号」生成，前端不传 userCode/orgId
      const created = await hq.admin.users.create(values);
      return { userCode: created.userCode, duplicated: created.emailDuplicated };
    },
    onSuccess: async (result) => {
      if (result.duplicated) {
        message.warning(`用户已创建：${result.userCode}（该邮箱已被其他账号使用，邮箱用于重设密码，建议唯一）`);
      } else {
        message.success(editing ? '用户已更新' : `用户已创建：${result.userCode}`);
      }
      setUserOpen(false);
      setEmailCheck(undefined);
      await invalidate();
      if (!editing) await queryClient.invalidateQueries({ queryKey: ['basic', 'user-preflight'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const saveRoles = useMutation({
    mutationFn: () => hq.admin.users.setRoles(roleTarget?.id ?? '', selectedRoles),
    onSuccess: async () => {
      message.success('角色已更新');
      setRoleOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '设置角色失败')
  });

  const savePerms = useMutation({
    mutationFn: async () => {
      const id = permRole?.id ?? '';
      // 先落权限勾选，再覆盖式保存字段屏蔽（后端只允许配置已分配的权限）
      await hq.admin.roles.setPerms(id, checkedPerms);
      const items = checkedPerms
        .map((permId) => ({ permId, fieldMask: permMasks[permId]?.trim() || null }))
        .filter((item) => item.fieldMask !== null);
      await hq.admin.roles.setPermMasks(id, items);
    },
    onSuccess: async () => {
      message.success('权限与字段屏蔽已更新');
      setPermRole(null);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存权限失败')
  });

  // 重置密码走 Form + rules（与个人中心 8~64 位同口径），不再用闭包变量 + 手写长度判断
  const resetPwd = useMutation({
    mutationFn: (values: { password: string }) => hq.admin.users.resetPassword(resetTarget?.id ?? '', values.password),
    onSuccess: () => {
      message.success('密码已重置');
      setResetTarget(null);
      resetForm.resetFields();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '重置密码失败')
  });

  const treeData = useMemo(() => {
    const rows = perms.data ?? [];
    const byParent = new Map<string, AdminPermRow[]>();
    rows.forEach((row) => {
      const list = byParent.get(row.parentId) ?? [];
      list.push(row);
      byParent.set(row.parentId, list);
    });
    const build = (parentId: string): PermNode[] =>
      (byParent.get(parentId) ?? []).map((row) => ({
        title: `${row.permName ?? row.permCode}（${row.permCode}）`,
        key: row.id,
        children: build(row.id)
      }));
    return build('0');
  }, [perms.data]);

  /** 状态中文（存量数据 ACTIVE / ENABLED 并存，两者都当启用）。 */
  const statusLabel = (value: string | null) => {
    const meta: Record<string, { text: string; color: string }> = {
      ACTIVE: { text: '启用', color: 'green' },
      ENABLED: { text: '启用', color: 'green' },
      DISABLED: { text: '停用', color: 'default' },
      LOCKED: { text: '锁定', color: 'red' },
      RESIGNED: { text: '离职', color: 'default' }
    };
    const hit = meta[value ?? ''] ?? { text: value ?? '-', color: 'default' };
    return <Tag color={hit.color}>{hit.text}</Tag>;
  };

  const userColumns: ColumnsType<AdminUserRow> = [
    { title: '账号', dataIndex: 'userCode', width: 150 },
    { title: '姓名', dataIndex: 'realName', width: 120, render: (value: string | null) => value || '-' },
    { title: '手机号', dataIndex: 'mobile', width: 140, render: (value: string | null) => value || '-' },
    { title: '机构', dataIndex: 'orgName', width: 180, render: (value: string | null) => value || '-' },
    { title: '等级', dataIndex: 'levelCode', width: 120, render: (value: string | null) => <Tag>{levelLabel(value)}</Tag> },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string | null) => statusLabel(value) },
    { title: '最后登录', dataIndex: 'lastLoginAt', width: 170, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      width: 300,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button
            type="link"
            onClick={() => {
              setEditing(row);
              userForm.setFieldsValue(row);
              setUserOpen(true);
            }}
          >
            编辑
          </Button>
          <Button type="link" icon={<SafetyOutlined />} onClick={() => openRoles(row)}>
            角色
          </Button>
          <Button
            type="link"
            icon={<KeyOutlined />}
            onClick={() => {
              setResetTarget(row);
              resetForm.resetFields();
            }}
          >
            重置密码
          </Button>
          <Button type="link" icon={<QrcodeOutlined />} onClick={() => setQrTarget(row)}>
            身份码
          </Button>
        </Space>
      )
    }
  ];

  const openRoles = async (row: AdminUserRow) => {
    setRoleTarget(row);
    setSelectedRoles([]);
    setRoleOpen(true);
    try {
      const current = await hq.admin.users.roles(row.id);
      setSelectedRoles(current.map((role) => role.id));
    } catch {
      /* 角色读取失败时留空，保存仍可覆盖 */
    }
  };

  // 两个权限任一即可进页面（页内按 tab 各自再判），
  // 不能用 canUser 卡死整页，否则有 sys:role:manage 的人永远进不了角色页
  if (!canUser && !canRole) {
    return (
      <Card className="page-card" title="人员与角色">
        <Alert
          type="warning"
          showIcon
          message="无权限"
          description="人员维护需要 sys:user:manage，角色维护需要 sys:role:manage，请联系系统管理员。"
        />
      </Card>
    );
  }

  return (
    <Card
      className="page-card"
      title={<Space>人员与角色<Typography.Text type="secondary">用户 / 角色权限</Typography.Text></Space>}
      extra={
        <Space>
          {canUser ? (
            <>
              <Input.Search allowClear placeholder="工号 / 姓名" style={{ width: 200 }} onSearch={(value) => setKeyword(value.trim())} />
              <Button icon={<ReloadOutlined />} onClick={() => invalidate()} loading={users.isFetching}>
                刷新
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditing(null);
                  userForm.resetFields();
                  userForm.setFieldsValue({ status: 'ACTIVE', levelCode: 'L3' });
                  setEmailCheck(undefined);
                  setUserOpen(true);
                }}
              >
                新增用户
              </Button>
            </>
          ) : null}
        </Space>
      }
    >
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'user',
            label: `用户（${users.data?.items.length ?? 0}）`,
            children: !canUser ? (
              <Alert type="warning" showIcon message="人员维护需要 sys:user:manage" />
            ) : (
              <>
                {users.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="用户加载失败"
                    description={users.error instanceof ApiError ? users.error.friendlyMessage : String(users.error)}
                  />
                ) : null}
                <Table<AdminUserRow>
                  rowKey="id"
                  size="middle"
                  columns={userColumns}
                  dataSource={users.data?.items ?? []}
                  loading={canUser && users.isPending}
                  pagination={false}
                  scroll={{ x: sumWidth(userColumns) }}
                />
                <Space style={{ marginTop: 16 }}>
                  <Typography.Text type="secondary">已加载 {users.data?.items.length ?? 0} 人</Typography.Text>
                  {users.data?.hasMore ? (
                    <Button onClick={() => setSize((prev) => prev + 20)} loading={users.isFetching}>
                      加载更多
                    </Button>
                  ) : null}
                </Space>
              </>
            )
          },
          {
            key: 'role',
            label: `角色（${roles.data?.length ?? 0}）`,
            children: !canRole ? (
              <Alert type="warning" showIcon message="角色维护需要 sys:role:manage" />
            ) : (
              <Table<AdminRoleRow>
                rowKey="id"
                size="middle"
                pagination={false}
                dataSource={roles.data ?? []}
                loading={canRole && roles.isPending}
                columns={[
                  { title: '角色编码', dataIndex: 'roleCode', width: 160 },
                  { title: '角色名称', dataIndex: 'roleName', width: 200, render: (value: string | null) => value || '-' },
                  { title: '门户', dataIndex: 'portal', width: 110 },
                  { title: '数据范围', dataIndex: 'dataScope', width: 130 },
                  { title: '内置', dataIndex: 'isBuiltin', width: 80, render: (value: boolean) => (value ? '是' : '否') },
                  {
                    title: '操作',
                    width: 140,
                    render: (_, row) =>
                      row.isBuiltin ? (
                        <Typography.Text type="secondary">内置角色不可改</Typography.Text>
                      ) : (
                        <Button type="link" onClick={() => setPermRole(row)}>
                          分配权限
                        </Button>
                      )
                  }
                ]}
              />
            )
          }
        ]}
      />

      <AccountQrModal
        hq={hq}
        open={Boolean(qrTarget)}
        userId={qrTarget?.id ?? ''}
        userName={qrTarget?.realName ?? qrTarget?.userCode}
        onClose={() => setQrTarget(null)}
      />

      <Modal
        title={editing ? '编辑用户' : '新增用户'}
        open={userOpen}
        onCancel={() => setUserOpen(false)}
        onOk={() => userForm.submit()}
        confirmLoading={saveUser.isPending}
        destroyOnHidden
      >
        <Form form={userForm} layout="vertical" onFinish={() => saveUser.mutate()} preserve={false}>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item
              label="账号"
              style={{ width: 250 }}
              extra={
                editing
                  ? '账号生成后不可修改'
                  : preflight.data && !preflight.data.userCode
                    ? '本机构无法生成账号（未配机构号或序号已用满），请联系运营方'
                    : '机构号 + 4 位序号自动生成'
              }
            >
              <Input
                readOnly
                disabled
                style={{ width: 220 }}
                value={editing?.userCode ?? preflight.data?.userCode ?? ''}
                placeholder={editing ? '' : '保存后自动生成'}
              />
            </Form.Item>
            <Form.Item name="realName" label="姓名" rules={[{ required: true, message: '必填' }]} style={{ width: 220 }}>
              <Input maxLength={64} />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item
              name="mobile"
              label="手机号"
              style={{ width: 220 }}
              rules={[{ pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }]}
            >
              <Input maxLength={11} placeholder="11 位手机号" />
            </Form.Item>
            <Form.Item
              name="email"
              label="邮箱"
              style={{ width: 260 }}
              rules={[
                { required: true, message: '邮箱必填' },
                { type: 'email', message: '邮箱格式不正确' }
              ]}
              extra={
                emailCount > 0 ? (
                  <Typography.Text type="warning">
                    已有 {emailCount} 个账号使用该邮箱；邮箱用于重设密码，不强制唯一，但建议一人一箱
                  </Typography.Text>
                ) : (
                  '邮箱用于重设密码'
                )
              }
            >
              <Input
                type="email"
                maxLength={128}
                placeholder="name@example.com"
                onBlur={(event) => setEmailCheck(event.target.value.trim() || undefined)}
              />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="levelCode" label="账号等级" rules={[{ required: true, message: '必选' }]} style={{ width: 180 }}>
              <Select options={LEVEL_OPTIONS} placeholder="选择等级" />
            </Form.Item>
            <Form.Item name="status" label="状态" style={{ width: 160 }}>
              {/* ACTIVE / ENABLED 是存量并存的两套写法，编辑时要能回显，否则下拉会直接显示原值 */}
              <Select
                options={[
                  { value: 'ACTIVE', label: '启用（ACTIVE）' },
                  { value: 'ENABLED', label: '启用（ENABLED）' },
                  { value: 'DISABLED', label: '停用' },
                  { value: 'LOCKED', label: '锁定' },
                  { value: 'RESIGNED', label: '离职' }
                ]}
              />
            </Form.Item>
            {!editing ? (
              <Form.Item
                name="password"
                label="初始密码"
                style={{ width: 220 }}
                rules={[
                  { required: true, message: '初始密码必填' },
                  { min: 8, message: '至少 8 位' },
                  { max: 64, message: '最多 64 位' }
                ]}
              >
                <Input.Password maxLength={64} placeholder="8~64 位" />
              </Form.Item>
            ) : null}
          </Space>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`分配角色 · ${roleTarget?.realName ?? ''}`}
        open={roleOpen}
        onCancel={() => setRoleOpen(false)}
        onOk={() => saveRoles.mutate()}
        confirmLoading={saveRoles.isPending}
      >
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="选择角色"
          value={selectedRoles}
          onChange={setSelectedRoles}
          options={(roles.data ?? []).map((role) => ({ value: role.id, label: `${role.roleName ?? role.roleCode}（${role.roleCode}）` }))}
        />
      </Modal>

      <Modal
        title={`分配权限 · ${permRole?.roleName ?? permRole?.roleCode ?? ''}`}
        open={Boolean(permRole)}
        onCancel={() => setPermRole(null)}
        onOk={() => savePerms.mutate()}
        confirmLoading={savePerms.isPending}
        width={640}
      >
        {perms.isError ? (
          <Alert type="error" showIcon message="权限清单加载失败" description={perms.error instanceof ApiError ? perms.error.friendlyMessage : ''} />
        ) : (
          <>
            <Tree
              checkable
              treeData={treeData}
              checkedKeys={checkedPerms}
              onCheck={(keys) => setCheckedPerms((Array.isArray(keys) ? keys : keys.checked) as string[])}
              height={300}
              defaultExpandAll
              selectable={false}
            />
            <Typography.Title level={5} style={{ marginTop: 16 }}>
              字段级屏蔽
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
              仅对已勾选权限生效，填字段名（逗号分隔，如 mobile,email），留空表示不屏蔽；多角色并集生效（任一角色屏蔽即屏蔽）。
            </Typography.Paragraph>
            {checkedPerms.length === 0 ? (
              <Alert type="info" showIcon message="先勾选权限，再配置该权限下要屏蔽的字段" />
            ) : (
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {checkedPerms.map((permId) => {
                  const perm = perms.data?.find((row) => row.id === permId);
                  return (
                    <Space key={permId} style={{ display: 'flex', marginBottom: 8 }} align="start">
                      <Typography.Text style={{ width: 330, display: 'inline-block' }} ellipsis={{ tooltip: perm?.permCode }}>
                        {perm ? `${perm.permName ?? perm.permCode}（${perm.permCode}）` : permId}
                      </Typography.Text>
                      <Input
                        size="small"
                        style={{ width: 220 }}
                        placeholder="如 mobile,email"
                        value={permMasks[permId] ?? ''}
                        onChange={(event) =>
                          setPermMasks((prev) => ({ ...prev, [permId]: event.target.value }))
                        }
                      />
                    </Space>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal
        title={`重置密码 · ${resetTarget?.realName ?? resetTarget?.userCode ?? ''}`}
        open={Boolean(resetTarget)}
        onCancel={() => setResetTarget(null)}
        onOk={() => resetForm.submit()}
        confirmLoading={resetPwd.isPending}
        okText="重置"
        destroyOnHidden
      >
        <Form form={resetForm} layout="vertical" onFinish={(values) => resetPwd.mutate(values)} preserve={false}>
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '新密码必填' },
              { min: 8, message: '至少 8 位' },
              { max: 64, message: '最多 64 位' }
            ]}
            extra="8~64 位；重置后 90 天内需再次修改"
          >
            <Input.Password maxLength={64} autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
